<script setup lang="ts">
/**
 * The entries of a period as a list grouped by local day — the agenda of a
 * calendar, the upcoming appointments on a dashboard. Only days with entries
 * appear; an entry spanning several days is listed on each, all-day entries
 * come first. Grouping follows `groupEventsByDay`: local calendar days (DST
 * cannot skip or repeat one), exclusive ends, a point in time on its start day.
 *
 * Nothing to show is an `NxEmptyState`; its button row is the `empty` slot.
 *
 * `layout="list"` is the desktop calendar's agenda: a date column whose day
 * sticks while its entries scroll by, one row per entry — dot, time span,
 * title over the location, calendar name and repeat glyph at the end, in
 * `NxEventChip`'s desktop look. Today's date is in the brand colour.
 * `dimPast` fades entries that are over.
 */
import { CalendarIcon } from '@blpde/nx-icons';
import { computed, inject } from 'vue';
import { NxEmptyState } from '../EmptyState';
import { dateTimeFormat } from '../Format/intlCache';
import { i18nInjectionKey } from '../internal';
import NxEventChip, { type NxEventChipStatusLabels } from './NxEventChip.vue';
import { groupEventsByDay, isEventPast, toIsoDate } from './eventDays';
import type { NxCalendarEvent, NxCalendarEventKey, NxEventDragStart } from './types';
import { useNow } from './useNow';

import type { NxCalendarEventAppearance } from './types';

const props = withDefaults(
  defineProps<{
    events: NxCalendarEvent[];
    eventAppearance?: NxCalendarEventAppearance;
    /** The period; `end` is exclusive. */
    range: { start: Date; end: Date };
    draggableEvents?: boolean;
    /** `cards`: a day rail and tinted pills. `list`: date column and one row per entry. */
    layout?: 'cards' | 'list';
    /** Fade entries whose end has passed (or that say `past`). */
    dimPast?: boolean;
    selectedEventId?: NxCalendarEventKey | null;
    locale?: string;
    ariaLabel?: string;
    emptyTitle?: string;
    emptyDescription?: string;
    untitledLabel?: string;
    allDayLabel?: string;
    statusLabels?: Partial<NxEventChipStatusLabels>;
  }>(),
  {
    draggableEvents: false,
    layout: 'cards',
    dimPast: false,
    selectedEventId: null,
    locale: undefined,
    ariaLabel: 'Agenda',
    emptyTitle: 'Keine Termine in diesem Zeitraum.',
    emptyDescription: undefined,
    untitledLabel: '(Ohne Titel)',
    allDayLabel: 'Ganztägig',
    statusLabels: () => ({}),
  }
);

const emit = defineEmits<{
  eventClick: [event: NxCalendarEvent, nativeEvent: MouseEvent];
  eventDblclick: [event: NxCalendarEvent, nativeEvent: MouseEvent];
  eventContextmenu: [event: NxCalendarEvent, nativeEvent: MouseEvent];
  eventDragStart: [payload: NxEventDragStart];
  eventDragEnd: [];
}>();

defineSlots<{
  /** Replaces the default `NxEventChip` of an entry. */
  event?: (props: { event: NxCalendarEvent; date: string }) => unknown;
  /** Buttons under the empty state („Neuer Termin“). */
  empty?: () => unknown;
}>();

const i18n = inject(i18nInjectionKey, null);
const resolvedLocale = computed(() => props.locale ?? i18n?.locale?.value ?? 'de-DE');
const now = useNow();
const todayIso = computed(() => toIsoDate(now.value));

const days = computed(() => groupEventsByDay(props.events, props.range));

const railFormat = computed(() =>
  dateTimeFormat(resolvedLocale.value, { weekday: 'short', month: 'short' })
);
const headingFormat = computed(() =>
  dateTimeFormat(resolvedLocale.value, {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  })
);

const listDayFormat = computed(() =>
  dateTimeFormat(resolvedLocale.value, { weekday: 'short', day: 'numeric', month: 'short' })
);

function isPast(event: NxCalendarEvent): boolean {
  return props.dimPast && isEventPast(event, now.value);
}

function onDragStart(nativeEvent: DragEvent, event: NxCalendarEvent): void {
  if (!props.draggableEvents || event.readonly) return;
  if (nativeEvent.dataTransfer) {
    nativeEvent.dataTransfer.effectAllowed = 'move';
    if (!nativeEvent.dataTransfer.types?.length) {
      nativeEvent.dataTransfer.setData('text/plain', String(event.id));
    }
  }
  emit('eventDragStart', { event, mode: 'move' });
}
</script>

<template>
  <section :aria-label="ariaLabel" class="nx-agenda-view">
    <NxEmptyState
      v-if="days.length === 0"
      :description="emptyDescription"
      :icon="CalendarIcon"
      :title="emptyTitle"
    >
      <template v-if="$slots.empty" #default><slot name="empty" /></template>
    </NxEmptyState>
    <ol v-else-if="layout === 'list'" class="nx-agenda-view__days is-list">
      <li
        v-for="day in days"
        :key="day.date"
        :class="['nx-agenda-view__day', { 'is-today': day.date === todayIso }]"
      >
        <h3
          :aria-current="day.date === todayIso ? 'date' : undefined"
          :aria-label="headingFormat.format(day.day)"
          class="nx-agenda-view__date"
        >
          {{ listDayFormat.format(day.day) }}
        </h3>
        <ul class="nx-agenda-view__events">
          <li
            v-for="event in day.events"
            :key="`${day.date}-${event.id}`"
            class="nx-agenda-view__event"
            @click="emit('eventClick', event, $event)"
            @contextmenu="emit('eventContextmenu', event, $event)"
            @dblclick="emit('eventDblclick', event, $event)"
            @dragend="emit('eventDragEnd')"
            @dragstart="onDragStart($event, event)"
          >
            <slot :date="day.date" :event="event" name="event">
              <NxEventChip
                :appearance="eventAppearance"
                :all-day-label="allDayLabel"
                :draggable="draggableEvents"
                :event="event"
                :locale="resolvedLocale"
                :past="isPast(event)"
                :selected="selectedEventId !== null && selectedEventId === event.id"
                :status-labels="statusLabels"
                :untitled-label="untitledLabel"
                look="desktop"
                show-time
                size="regular"
                variant="dot"
              />
            </slot>
          </li>
        </ul>
      </li>
    </ol>
    <ol v-else class="nx-agenda-view__days">
      <li
        v-for="day in days"
        :key="day.date"
        :class="['nx-agenda-view__day', { 'is-today': day.date === todayIso }]"
      >
        <div class="nx-agenda-view__rail" aria-hidden="true">
          <strong>{{ day.day.getDate() }}</strong>
          <span>{{ railFormat.format(day.day) }}</span>
        </div>
        <div class="nx-agenda-view__content">
          <h3 :aria-current="day.date === todayIso ? 'date' : undefined">
            {{ headingFormat.format(day.day) }}
          </h3>
          <ul class="nx-agenda-view__events">
            <li
              v-for="event in day.events"
              :key="`${day.date}-${event.id}`"
              class="nx-agenda-view__event"
              @click="emit('eventClick', event, $event)"
              @contextmenu="emit('eventContextmenu', event, $event)"
              @dblclick="emit('eventDblclick', event, $event)"
              @dragend="emit('eventDragEnd')"
              @dragstart="onDragStart($event, event)"
            >
              <slot :date="day.date" :event="event" name="event">
                <NxEventChip
                  :appearance="eventAppearance"
                  :all-day-label="allDayLabel"
                  :draggable="draggableEvents"
                  :event="event"
                  :locale="resolvedLocale"
                  :past="isPast(event)"
                  :selected="selectedEventId !== null && selectedEventId === event.id"
                  :status-labels="statusLabels"
                  :untitled-label="untitledLabel"
                  show-time
                  size="regular"
                />
              </slot>
            </li>
          </ul>
        </div>
      </li>
    </ol>
  </section>
</template>

<style scoped>
.nx-agenda-view {
  container-type: inline-size;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  color: var(--nxt-fg);
}

.nx-agenda-view__days {
  flex: 1;
  min-height: 0;
  margin: 0;
  padding: 0;
  overflow-y: auto;
  list-style: none;
  font-size: inherit;
  scrollbar-width: thin;
}

.nx-agenda-view__day {
  display: grid;
  grid-template-columns: var(--nxt-area-size-xs) minmax(0, 1fr);
  gap: var(--nxt-space-md);
  padding: var(--nxt-space-md) 0;
  border-block-end: var(--nxt-hairline) solid var(--nxt-border);
}

.nx-agenda-view__rail {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--nxt-space-2xs);
  font-variant-numeric: tabular-nums;
}

.nx-agenda-view__rail strong {
  font-size: var(--nxt-font-size-2xl);
  font-weight: var(--nxt-font-weight-semibold);
  line-height: var(--nxt-line-height-tight);
}

.nx-agenda-view__rail span {
  color: var(--nxt-fg-muted);
  font-size: var(--nxt-font-size-xs);
  text-align: center;
}

.nx-agenda-view__day.is-today .nx-agenda-view__rail {
  color: var(--nxt-brand-bg);
}

.nx-agenda-view__content h3 {
  margin: 0 0 var(--nxt-space-xs);
  color: var(--nxt-fg-muted);
  font-size: var(--nxt-font-size-xs);
  font-weight: var(--nxt-font-weight-medium);
}

.nx-agenda-view__events {
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-xs);
  margin: 0;
  padding: 0;
  list-style: none;
  font-size: inherit;
}

.nx-agenda-view__days:not(.is-list) .nx-agenda-view__event :deep(.nx-event-chip) {
  padding: var(--nxt-space-xs) var(--nxt-space-sm);
}

/* --- list: date column, one row per entry ------------------------------- */

.nx-agenda-view__days.is-list .nx-agenda-view__day {
  grid-template-columns: calc(var(--nxt-size-unit) * 9) minmax(0, 1fr);
  gap: var(--nxt-space-sm);
  padding: var(--nxt-space-xs) 0;
}

/* The day stays at the top while its entries scroll past. */
.nx-agenda-view__date {
  position: sticky;
  top: 0;
  z-index: 1;
  align-self: start;
  margin: 0;
  padding: var(--nxt-space-xs) var(--nxt-space-sm);
  background: var(--nxt-surface-bg);
  color: var(--nxt-fg);
  font-size: var(--nxt-font-size-sm);
  font-weight: var(--nxt-font-weight-medium);
  white-space: nowrap;
}

.nx-agenda-view__day.is-today .nx-agenda-view__date {
  color: var(--nxt-link);
}

.nx-agenda-view__days.is-list .nx-agenda-view__events {
  gap: 0;
}

@container (max-width: 35em) {
  .nx-agenda-view__day {
    grid-template-columns: var(--nxt-control-size-lg) minmax(0, 1fr);
    gap: var(--nxt-space-sm);
  }

  /* Narrow: the date becomes a sticky band over its entries. */
  .nx-agenda-view__days.is-list .nx-agenda-view__day {
    grid-template-columns: minmax(0, 1fr);
    gap: 0;
  }

  .nx-agenda-view__days.is-list .nx-agenda-view__date {
    border-block-end: var(--nxt-hairline) solid var(--nxt-border-subtle);
  }
}
</style>
